import Layout from "../../components/Layout/Layout";
import BreadCrumb from "../../components/BreadCrumb/Breadcrumb";
import Button from "../../components/Button/Button";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import useStyles from "./styles";
import Modal from "../../components/Modal";

import { httprequest } from "../../data/api";
import { toast, ToastContainer } from "react-toastify";
const SUBMISSION_FLAGS_ENDPOINT = "/admin/v1/lesson-note-reviews";

const SubmissionReview = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [flagDetail, setFlagDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [adjustPoint, setAdjustPoint] = useState("");
  const [decisionReason, setDecisionReason] = useState("");
  const [modalType, setModalType] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchFlagDetail = () => {
    if (!id) {
      return;
    }
    setIsLoading(true);
    httprequest
      .get(`${SUBMISSION_FLAGS_ENDPOINT}/${id}`)
      .then((res) => {
        const data = res?.data?.data || res?.data || null;
        setFlagDetail(data);

        const review = data?.review || {};
        const points = review.adjusted_points ?? review.original_points ?? review.awarded_points ?? review.points;
        setAdjustPoint(points != null ? String(points) : "");
        setDecisionReason(review.decision_reason || review.review_note || "");
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.message || "Failed to load submission review");
      });
  };

  useEffect(() => {
    fetchFlagDetail();
  }, []);

  const review = flagDetail?.review || {};
  const lessonNoteData = flagDetail?.lesson_note?.lesson_note || {};
  const outlines = flagDetail?.lesson_note?.lesson_outlines || [];
  const outline = outlines.length > 0 ? outlines[0] : {};

  const topic = outline.topic || `Lesson Note (${lessonNoteData?.subject?.name || "Topic"})`;
  const objective = outline.learning_goals || "";
  const outcome = outline.training_method || "";

  const contentHtml = outline.lesson_outline || "";

  const signalTitle = flagDetail?.flag_type || "Submission Review";
  const signalDetail = flagDetail?.description || review.review_note || review.decision_reason || "Please review the lesson note submission.";
  const signalRange = review.created_at ? `Submitted on ${new Date(review.created_at).toLocaleString(navigator.language, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : "";

  const originalPoint = review.original_points != null ? `${review.original_points} pts` : "";

  const handleApprove = () => setModalType("approve");
  const handleReject = () => setModalType("reject");
  const closeModal = () => setModalType(null);

  const processReview = async () => {
    if (!id || !modalType) return;
    setIsProcessing(true);

    const adjustedPointsNum = Number(adjustPoint?.replace(/[^0-9.-]+/g, "")) || 0;

    const payload = {
      adjusted_points: adjustedPointsNum,
      decision_reason: decisionReason
    };

    try {
      const endpoint = `${SUBMISSION_FLAGS_ENDPOINT}/${id}/${modalType}`;
      await httprequest.patch(endpoint, payload);
      toast.success(`Submission ${modalType === "approve" ? "approved" : "rejected"} successfully`);
      navigate("/rewards");
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to ${modalType} submission`);
    } finally {
      setIsProcessing(false);
      closeModal();
    }
  };

  return (
    <Layout>
      <BreadCrumb
        link_one="/rewards"
        link_one_name="Rewards"
        active="Submission Review"
        description="Review detected quality signals before approving reward points"
      />
      <div className={classes.reviewContainer}>
        <div className={classes.reviewLeft}>
          <div className={classes.outlineCard}>
            <h5 className={classes.outlineTitle}>
              Submitted Lesson Outline (Preview)
            </h5>
            <div className={classes.outlineRow}>
              <span className={classes.outlineLabel}>Topic :</span>
              <span>{topic}</span>
            </div>
            {objective && (
              <div className={classes.outlineRow}>
                <span className={classes.outlineLabel}>Objective:</span>
                <span>{objective}</span>
              </div>
            )}
            {outcome && (
              <div className={classes.outlineRow}>
                <span className={classes.outlineLabel}>Outcome:</span>
                <span>{outcome}</span>
              </div>
            )}
          </div>

          <div className={classes.contentCard}>
            <h5>Submission Content</h5>
            {isLoading && <p>Loading submission content...</p>}
            {!isLoading && !contentHtml && <p>No additional content available.</p>}
            {!isLoading && contentHtml && (
              <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
            )}
          </div>
        </div>

        <div className={classes.reviewRight}>
          <div className={classes.signalsCard}>
            <h4 className={classes.signalsTitle}>
              Detected Quality Signals
            </h4>
            <p className={classes.signalsSubtitle}>
              Signals indicate deviations from normal submission patterns.
            </p>
            <div className={classes.signalAlert}>
              <h5 className={classes.signalAlertTitle}>
                {signalTitle}
              </h5>
              <p className={classes.signalAlertGreen}>
                {signalDetail}
              </p>
              <p className={classes.signalAlertGrey}>
                {signalRange}
              </p>
            </div>
          </div>

          <div className={classes.decisionCard}>
            <h4 className={classes.decisionTitle}>Points Decision</h4>

            <p className={classes.decisionLabel}>Original Point</p>
            <input
              className={classes.decisionInput}
              type="text"
              value={originalPoint}
              disabled
            />

            <p className={classes.decisionLabel}>Adjust Point</p>
            <input
              className={classes.decisionInput}
              type="text"
              value={adjustPoint}
              onChange={(e) => setAdjustPoint(e.target.value)}
            />

            <p className={classes.decisionLabel}>
              Decision Reason (required)
            </p>
            <textarea
              className={classes.decisionTextarea}
              value={decisionReason}
              onChange={(e) => setDecisionReason(e.target.value)}
            />

            <div className={classes.decisionActions}>
              <Button
                variant="dangeroutline"
                buttonSize="medium"
                color="btndanger"
                buttonName="Reject Submission"
                onClick={handleReject}
              />
              <Button
                variant="primary"
                buttonSize="medium"
                color="btndefault"
                buttonName="Approve Submission"
                onClick={handleApprove}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={modalType === "approve" ? "Confirm Approval" : "Confirm Rejection"}
        subtitle={`Are you sure you want to ${modalType} this submission?`}
        modalContent={
          <div style={{ padding: 24, paddingBottom: 0 }}>
            <p style={{ marginBottom: 12 }}>
              <strong>Adjusted Points:</strong> {adjustPoint || "0"}{!String(adjustPoint).includes("pts") && " pts"}
            </p>
            <p>
              <strong>Reason:</strong> {decisionReason || "None provided"}
            </p>
          </div>
        }
        modalFooter={
          <div className="flex gap-10">
            <Button
              variant="dangeroutline"
              buttonSize="full"
              color="btndanger"
              buttonName="Cancel"
              onClick={closeModal}
              disabled={isProcessing}
            />
            <Button
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName={isProcessing ? "Processing..." : `Confirm ${modalType === "approve" ? "Approval" : "Rejection"}`}
              onClick={processReview}
              disabled={isProcessing}
            />
          </div>
        }
        isopen={!!modalType}
        closeModal={closeModal}
      />
      <ToastContainer />
    </Layout>
  );
};

export default SubmissionReview;
